import React, { useEffect } from 'react';
import './Modal.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showCloseButton?: boolean;
  className?: string;
  footer?: React.ReactNode;
}

/**
 * Reusable Modal component with consistent styling
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true,
  className = '',
  footer
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClass = `common-modal--${size}`;
  const modalClassName = className ? `common-modal ${sizeClass} ${className}` : `common-modal ${sizeClass}`;

  return (
    <div className="common-modal-overlay" onClick={onClose}>
      <div className={modalClassName} onClick={(e) => e.stopPropagation()}>
        {(title || showCloseButton) && (
          <div className="common-modal-header">
            {title && <h2 className="common-modal-title">{title}</h2>}
            {showCloseButton && (
              <button 
                className="common-modal-close" 
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="common-modal-body">
          {children}
        </div>
        {footer && (
          <div className="common-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;

